import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const RegistrationForm = ({ onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    language: 'fr',
    referralCode: '',
    acceptTerms: false,
    marketingConsent: false
  });

  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const languageOptions = [
    { value: 'fr', label: 'Français' },
    { value: 'en', label: 'English' }
  ];

  const validatePassword = (password) => {
    let strength = 0;
    if (password?.length >= 8) strength++;
    if (/[A-Z]/?.test(password)) strength++;
    if (/[a-z]/?.test(password)) strength++;
    if (/[0-9]/?.test(password)) strength++;
    if (/[^A-Za-z0-9]/?.test(password)) strength++;
    return strength;
  };

  const getPasswordStrengthText = () => {
    const texts = {
      fr: ['Très faible', 'Faible', 'Moyen', 'Fort', 'Très fort'],
      en: ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong']
    };
    return texts?.[formData?.language]?.[passwordStrength] || texts?.[formData?.language]?.[0];
  };

  const getPasswordStrengthColor = () => {
    const colors = ['bg-error', 'bg-warning', 'bg-accent', 'bg-success', 'bg-primary'];
    return colors?.[passwordStrength] || colors?.[0];
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.firstName?.trim()) {
      newErrors.firstName = formData?.language === 'fr' ? 'Le prénom est requis' : 'First name is required';
    }

    if (!formData?.lastName?.trim()) {
      newErrors.lastName = formData?.language === 'fr' ? 'Le nom est requis' : 'Last name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = formData?.language === 'fr' ? 'L\'email est requis' : 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = formData?.language === 'fr' ? 'Format d\'email invalide' : 'Invalid email format';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = formData?.language === 'fr' ? 'Le numéro de téléphone est requis' : 'Phone number is required';
    } else if (!/^6[0-9]{8}$/?.test(formData?.phone)) {
      newErrors.phone = formData?.language === 'fr' ? 'Format: 6XXXXXXXX' : 'Format: 6XXXXXXXX';
    }

    if (!formData?.password) {
      newErrors.password = formData?.language === 'fr' ? 'Le mot de passe est requis' : 'Password is required';
    } else if (passwordStrength < 3) {
      newErrors.password = formData?.language === 'fr' ? 'Mot de passe trop faible' : 'Password too weak';
    }

    if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = formData?.language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match';
    }

    if (!formData?.acceptTerms) {
      newErrors.acceptTerms = formData?.language === 'fr' ? 'Vous devez accepter les conditions' : 'You must accept the terms';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'password') {
      setPasswordStrength(validatePassword(value));
    }
    
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Personal Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground">
          {formData?.language === 'fr' ? 'Informations personnelles' : 'Personal Information'}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={formData?.language === 'fr' ? 'Prénom' : 'First Name'}
            type="text"
            placeholder={formData?.language === 'fr' ? 'Entrez votre prénom' : 'Enter your first name'}
            value={formData?.firstName}
            onChange={(e) => handleInputChange('firstName', e?.target?.value)}
            error={errors?.firstName}
            required
          />
          
          <Input
            label={formData?.language === 'fr' ? 'Nom' : 'Last Name'}
            type="text"
            placeholder={formData?.language === 'fr' ? 'Entrez votre nom' : 'Enter your last name'}
            value={formData?.lastName}
            onChange={(e) => handleInputChange('lastName', e?.target?.value)}
            error={errors?.lastName}
            required
          />
        </div>
      </div>
      {/* Contact Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground">
          {formData?.language === 'fr' ? 'Informations de contact' : 'Contact Information'}
        </h3>
        
        <Input
          label={formData?.language === 'fr' ? 'Adresse email' : 'Email Address'}
          type="email"
          placeholder={formData?.language === 'fr' ? 'exemple@email.com' : 'example@email.com'}
          value={formData?.email}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          error={errors?.email}
          required
          description={formData?.language === 'fr' ? 'Utilisé pour la connexion et les notifications' : 'Used for login and notifications'}
        />
        
        <Input
          label={formData?.language === 'fr' ? 'Numéro de téléphone' : 'Phone Number'}
          type="tel"
          placeholder="6XXXXXXXX"
          value={formData?.phone}
          onChange={(e) => handleInputChange('phone', e?.target?.value)}
          error={errors?.phone}
          required
          description={formData?.language === 'fr' ? 'Format camerounais requis' : 'Cameroon format required'}
        />
      </div>
      {/* Password Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground">
          {formData?.language === 'fr' ? 'Sécurité du compte' : 'Account Security'}
        </h3>
        
        <div className="relative">
          <Input
            label={formData?.language === 'fr' ? 'Mot de passe' : 'Password'}
            type={showPassword ? 'text' : 'password'}
            placeholder={formData?.language === 'fr' ? 'Créez un mot de passe fort' : 'Create a strong password'}
            value={formData?.password}
            onChange={(e) => handleInputChange('password', e?.target?.value)}
            error={errors?.password}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-8 text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
          </button>
        </div>

        {/* Password Strength Indicator */}
        {formData?.password && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {formData?.language === 'fr' ? 'Force du mot de passe:' : 'Password strength:'}
              </span>
              <span className="text-sm font-medium text-foreground">
                {getPasswordStrengthText()}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                style={{ width: `${(passwordStrength / 5) * 100}%` }}
              />
            </div>
          </div>
        )}
        
        <div className="relative">
          <Input
            label={formData?.language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm Password'}
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder={formData?.language === 'fr' ? 'Répétez votre mot de passe' : 'Repeat your password'}
            value={formData?.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
            error={errors?.confirmPassword}
            required
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-8 text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={16} />
          </button>
        </div>
      </div>
      {/* Language & Referral */}
      <div className="space-y-4">
        <Select
          label={formData?.language === 'fr' ? 'Langue préférée' : 'Preferred Language'}
          options={languageOptions}
          value={formData?.language}
          onChange={(value) => handleInputChange('language', value)}
          description={formData?.language === 'fr' ? 'Langue de l\'interface utilisateur' : 'User interface language'}
        />
        
        <Input
          label={formData?.language === 'fr' ? 'Code de parrainage (optionnel)' : 'Referral Code (Optional)'}
          type="text"
          placeholder={formData?.language === 'fr' ? 'Entrez le code de parrainage' : 'Enter referral code'}
          value={formData?.referralCode}
          onChange={(e) => handleInputChange('referralCode', e?.target?.value)}
          description={formData?.language === 'fr' ? 'Obtenez des réductions et avantages' : 'Get discounts and benefits'}
        />
      </div>
      {/* Terms and Conditions */}
      <div className="space-y-4">
        <Checkbox
          label={
            <span className="text-sm">
              {formData?.language === 'fr' ? 'J\'accepte les ' : 'I accept the '}
              <button type="button" className="text-primary hover:underline">
                {formData?.language === 'fr' ? 'conditions d\'utilisation' : 'terms of service'}
              </button>
              {formData?.language === 'fr' ? ' et la ' : ' and '}
              <button type="button" className="text-primary hover:underline">
                {formData?.language === 'fr' ? 'politique de confidentialité' : 'privacy policy'}
              </button>
            </span>
          }
          checked={formData?.acceptTerms}
          onChange={(e) => handleInputChange('acceptTerms', e?.target?.checked)}
          error={errors?.acceptTerms}
          required
        />
        
        <Checkbox
          label={formData?.language === 'fr' ? 'Je souhaite recevoir des offres et promotions par email' : 'I want to receive offers and promotions by email'}
          checked={formData?.marketingConsent}
          onChange={(e) => handleInputChange('marketingConsent', e?.target?.checked)}
        />
      </div>
      {/* Anti-fraud Warning */}
      <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={20} className="text-warning mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-warning mb-1">
              {formData?.language === 'fr' ? 'Protection Anti-Fraude' : 'Anti-Fraud Protection'}
            </p>
            <p className="text-muted-foreground">
              {formData?.language === 'fr' ?'AutoCasse vérifie tous les comptes. Ne partagez jamais vos informations personnelles en dehors de la plateforme.' :'AutoCasse verifies all accounts. Never share your personal information outside the platform.'
              }
            </p>
          </div>
        </div>
      </div>
      {/* Submit Button */}
      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={isLoading}
        iconName="UserPlus"
        iconPosition="left"
        iconSize={16}
        className="mt-6"
      >
        {formData?.language === 'fr' ? 'Créer mon compte' : 'Create My Account'}
      </Button>
    </form>
  );
};

export default RegistrationForm;